import { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/authStore';
import { useSaleNotificationsSocket } from './useSaleNotificationsSocket';
import { refreshKeysFor } from './listRefresh';

/**
 * Ventana de agrupado. «Mis ventas» encadena hasta 20 páginas, y varias cuentas resolviéndose
 * juntas es el caso normal: sin ella, cada aviso de la ráfaga barre el listado entero.
 */
export const REFRESH_DEBOUNCE_MS = 1_500;

/**
 * Puente del canal de avisos: si el vendedor está en una lista del portal que pinta el estado de
 * sus ventas, la vuelve a pedir. Sin aviso, sin botón y sin interrumpirlo.
 */
export function SaleNotificationsBridge(): null {
    const queryClient = useQueryClient();
    const isSignedIn = useAuthStore((state) => state.user !== null);
    const pathname = useRouterState({ select: (state) => state.location.pathname });

    // Por ref porque el aviso se agrupa: al refrescar vale la pantalla de ESE momento, no la de
    // cuando llegó el primer aviso de la ráfaga.
    const pathnameRef = useRef(pathname);
    useEffect(() => {
        pathnameRef.current = pathname;
    }, [pathname]);

    const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    // Un refresco que dispara con la pantalla ya desmontada no tiene a quién repintar.
    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
        };
    }, []);

    const onNotification = useCallback(() => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            for (const queryKey of refreshKeysFor(pathnameRef.current)) {
                // Solo lo montado: una lista que comparte key con otra pantalla no se pide de más.
                void queryClient.invalidateQueries({ queryKey, refetchType: 'active' });
            }
        }, REFRESH_DEBOUNCE_MS);
    }, [queryClient]);

    useSaleNotificationsSocket({ enabled: isSignedIn, onNotification });

    return null;
}
